import { useCallback, useRef } from 'react';
import type { AnalyticEventName } from 'modules/analytics/analytics';
import analytics from 'modules/analytics';

export function useTrackFormFieldEdit<T>({
  metricName,
  isEnabled,
  additionalData,
}: {
  metricName: AnalyticEventName;
  isEnabled: boolean;
  additionalData?: Record<string, unknown>;
}) {
  const originalValueRef = useRef<T | null>(null);
  const hasTrackedOriginal = useRef(false);

  const captureOriginalValue = useCallback((value: T) => {
    if (!hasTrackedOriginal.current) {
      originalValueRef.current = value;
      hasTrackedOriginal.current = true;
    }
  }, []);

  const trackEdit = useCallback(
    (finalValue: T) => {
      if (!isEnabled || !hasTrackedOriginal.current) return;
      if (originalValueRef.current !== finalValue) {
        analytics.track(metricName, {
          original_value: originalValueRef.current,
          final_value: finalValue,
          ...additionalData,
        });
      }
      hasTrackedOriginal.current = false;
      originalValueRef.current = null;
    },
    [isEnabled, metricName, additionalData]
  );

  return { captureOriginalValue, trackEdit };
}
